import Breadcrumb from '../../components/feature/Breadcrumb';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../hooks/useSEO';
import { useLocation } from 'react-router-dom';

const KnowledgePage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');

  useSEO({
    title: isEnglish ? 'Helpful Knowledge' : '役立ち関連知識',
    description: isEnglish 
      ? 'Basic knowledge of post-processing and finishing for 3D prints. Practical information including support removal, sanding, painting, screw fastening, insert nuts, and 3D data acquisition methods.'
      : '3Dプリント後の加工・後処理の基礎知識。サポート材除去、ヤスリがけ、塗装、ネジ締結、インサートナット、3Dデータ入手方法など実用的な情報を掲載。',
    keywords: isEnglish
      ? '3D printer,post-processing,finishing,sanding,painting,screws,insert nuts,3D data,CAD'
      : '3Dプリンター,後処理,加工,ヤスリ,塗装,ネジ,インサートナット,3Dデータ,CAD',
    canonicalPath: isEnglish ? '/en/knowledge' : '/knowledge'
  });

  const breadcrumbItems = isEnglish
    ? [
        { label: 'Home', path: '/en' },
        { label: 'Helpful Knowledge', path: '/en/knowledge' }
      ]
    : [
        { label: 'ホーム', path: '/' },
        { label: '役立ち関連知識', path: '/knowledge' }
      ];

  const TEXT = isEnglish ? {
    pageTitle: 'Helpful Knowledge',
    postProcessing: {
      title: 'Post-Processing Basics',
      support: {
        title: 'What is Support Material',
        intro: 'Support material is an auxiliary structure that supports floating parts or steep angles during 3D printing. It needs to be removed after printing.',
        whenNeeded: 'When Support is Needed',
        whenNeededList: [
          'Parts with overhang angles of 45 degrees or more',
          'Floating structures (bridge-like parts, etc.)',
          'Thin protrusions or complex shapes'
        ],
        removalSteps: 'Basic Support Removal Steps',
        removalStepsList: [
          'Roughly remove with nippers or pliers',
          'Cut fine parts with a cutter',
          'Smooth the traces with sandpaper'
        ]
      },
      sanding: {
        title: 'Sanding Basics',
        intro: 'Sandpaper grit is indicated by number (#), with higher numbers being finer. The basic approach is to gradually move to finer grits.',
        coarse: '#120~#240 (Coarse)',
        coarseDesc: 'Used for removing support traces and correcting large irregularities. Strong cutting power, used in the initial shaping stage.',
        medium: '#400~#600 (Medium)',
        mediumDesc: 'Used for surface smoothing and reducing layer lines. Suitable for intermediate finishing after coarse sanding.',
        fine: '#800~#1200 (Fine)',
        fineDesc: 'Used for final polishing and pre-painting surface preparation. Can smooth the surface and create a glossy finish.',
        tip: 'Using wet sanding paper reduces dust and provides a smoother finish.'
      },
      deburring: {
        title: 'Deburring with Cutter',
        intro: 'Burrs are thread-like protrusions created during printing or small protrusions remaining after support removal. Remove them carefully with a cutter.',
        basicUse: 'Basic Usage',
        basicUseList: [
          'Lay the blade flat and use in a scraping motion',
          'Remove gradually, be careful not to over-cut',
          'Can also be used for chamfering edges'
        ],
        safety: 'Safe Working Methods',
        safetyList: [
          'Always move the blade away from your body',
          'Use a work surface, do not hold in hand',
          'Replace blade immediately when dull'
        ]
      },
      heatGun: {
        title: 'Simple Finishing with Heat Gun',
        intro: 'By lightly melting the surface with a heat gun, you can make layer lines less noticeable and achieve a smooth finish.',
        effects: 'Effects',
        effectsList: [
          'Make layer lines less noticeable',
          'Add gloss to the surface',
          'Reduce sanding effort'
        ],
        cautions: 'Cautions',
        cautionsList: [
          'Do not apply to one spot continuously (causes deformation)',
          'Work in a well-ventilated area',
          'Appropriate temperature varies by material'
        ]
      },
      rotaryTool: {
        title: 'Fine Adjustment with Rotary Tool',
        intro: 'An electric small grinding tool used for carving fine parts, drilling holes, and polishing.',
        uses: 'Main Uses',
        usesList: [
          'Carving fine parts',
          'Hole expansion and shape correction',
          'Finishing with polishing bits',
          'Engraving and decorative work'
        ],
        bits: 'Bit Types',
        bitsList: [
          'Grinding bits: for carving',
          'Diamond bits: for hard materials',
          'Felt bits: for polishing finish',
          'Cutting bits: for cutting'
        ]
      },
      painting: {
        title: 'Painting Super Basics',
        intro: 'Painting consists of three steps. Performing each step carefully results in a beautiful finish.',
        primer: '1. Primer (Base)',
        primerDesc: 'Base treatment that improves paint adhesion and color development.',
        primerList: [
          'Smooth the surface with sandpaper',
          'Remove oil with degreaser',
          'Apply primer thinly and evenly',
          'Let it dry completely'
        ],
        paint: '2. Painting (Main Coat)',
        paintDesc: 'The step of applying color. The trick is to apply thin coats repeatedly.',
        paintList: [
          'Do not apply thick in one coat, apply thinly',
          'Follow drying time (15-30 minutes)',
          'Apply 2-3 coats',
          'Check for unevenness'
        ],
        clear: '3. Clear Coat (Finish)',
        clearDesc: 'Transparent coating for protection and gloss.',
        clearList: [
          'After main coat is completely dry',
          'Can choose glossy or matte',
          'Apply thinly about 2 times',
          'Let dry for 24 hours or more'
        ],
        tip: 'Always work in a well-ventilated area and wear a mask. Spray painting is ideally done outdoors.'
      }
    },
    screws: {
      title: 'Screws and Fastening Basics',
      types: {
        title: 'Screw Types',
        intro: 'Two main types of screws are used for fastening 3D printed parts. Use them according to the application.',
        tapping: 'Tapping Screws',
        tappingDesc: 'Screws that can be screwed directly into resin. They create their own threads as they tighten.',
        tappingFeatures: 'Features:',
        tappingFeaturesList: [
          'Can be used with just a pilot hole (no threads needed)',
          'Easy to install',
          'Becomes loose with repeated removal'
        ],
        tappingSuited: 'Suitable Uses:',
        tappingSuitedList: [
          'Parts that will not be removed once assembled',
          'Light load fastening',
          'Temporary assembly or prototypes'
        ],
        machine: 'Machine Screws (Bolts)',
        machineDesc: 'Screws that require threads in nuts or metal. Used in combination with insert nuts.',
        machineFeatures: 'Features:',
        machineFeaturesList: [
          'Requires nuts or insert nuts',
          'Can be removed and installed repeatedly',
          'Strong fastening possible'
        ],
        machineSuited: 'Suitable Uses:',
        machineSuitedList: [
          'Parts that need to be removed for maintenance',
          'Fastening requiring strength',
          'Product-level assembly'
        ]
      },
      insertNuts: {
        title: 'What are Insert Nuts',
        intro: 'Insert nuts (heat-set nuts) are brass nuts that can be embedded in resin with heat. They can create metal screw holes in 3D printed parts and are a very compatible fastening method.',
        benefits: 'Benefits',
        benefitsList: [
          'Can be removed and installed repeatedly',
          'Threads do not get damaged',
          'Strong fastening possible',
          'Professional quality finish'
        ],
        tools: 'Required Tools',
        toolsList: [
          'Soldering iron (about 40W)',
          'Insert nuts',
          'Appropriate pilot hole (during design)'
        ],
        steps: 'Installation Steps',
        stepsList: [
          'Heat with soldering iron',
          'Press in vertically',
          'Until flush with surface',
          'Hold until cooled'
        ],
        tip: 'Insert nuts are important parts for finishing 3D printed items to product level. Always use them for parts that require maintenance.'
      },
      sizes: {
        title: 'Meaning of M3 / M2, etc.',
        intro: 'In screw size notation, "M" indicates metric thread standard, and the following number represents thickness (diameter).',
        reading: 'How to Read Sizes',
        m2: 'M2: 2mm diameter screw',
        m3: 'M3: 3mm diameter screw',
        m4: 'M4: 4mm diameter screw',
        m5: 'M5: 5mm diameter screw',
        common: 'Commonly Used Sizes in 3D Printing',
        m2Use: 'M2: Small electronic devices, precision parts, miniature models, etc.',
        m3Use: 'M3: Most common. Small to medium parts, electronics projects, hobby use',
        m4Use: 'M4: Medium parts, areas requiring some strength',
        m5Use: 'M5: Large parts, structural materials, high-load areas'
      }
    },
    dataAcquisition: {
      title: '3D Data Acquisition Methods',
      intro: '3D data required for 3D printing can be obtained mainly through three methods. Use them according to your purpose.',
      download: {
        title: '1. Download from the Internet',
        intro: 'There are many model hosting sites for 3D printing, with a vast number of models published both free and paid.',
        thingiverse: 'Thingiverse: The largest free 3D model sharing site. Wide range from hobby to practical items.',
        thingiverseFeature: 'Features: Completely free, active community',
        printables: 'Printables: Free site operated by Prusa Research. Many high-quality models.',
        printablesFeature: 'Features: Quality-focused, contests held',
        cults: 'Cults3D: Mix of free and paid. Rich in design-oriented models.',
        cultsFeature: 'Features: Designer works, paid models available',
        nextprint: 'Nextprint: 3D model sharing platform operated by ELEGOO. Users worldwide can upload and download 3D data such as STL.',
        nextprintFeature: 'Features: Supports profiles and one-click printing for ELEGOO printers, while also offering versatile 3D models usable with other manufacturers\' printers.',
        tip: 'Check the license before using downloaded models. Commercial use may be prohibited in some cases.'
      },
      design: {
        title: '2. Design Yourself',
        intro: 'By creating models yourself, you can realize completely original shapes. Use two types of software according to the application.',
        blender: 'Design Models: Blender',
        blenderFeature: 'Features: Good at creating free-form shapes. Suitable for figures, characters, and organic shapes.',
        blenderSuited: 'Suitable Uses:',
        blenderSuitedList: [
          'Figures and characters',
          'Decorative items and art works',
          'Sculptural shapes',
          'Animation production'
        ],
        blenderConcept: 'Shape Creation Concept: Like kneading clay, manipulate vertices, edges, and faces to create shapes',
        fusion: 'CAD Models: Autodesk Fusion (formerly Fusion360)',
        fusionFeature: 'Features: Can design with precise dimensions. Suitable for mechanical parts, practical items, and assembled products.',
        fusionSuited: 'Suitable Uses:',
        fusionSuitedList: [
          'Mechanical parts and industrial products',
          'Precision work such as screw holes',
          'Assembled products',
          'Practical tools and jigs'
        ],
        fusionConcept: 'Shape Creation Concept: Create shapes through engineering methods such as drawing sketches and extruding or rotating them',
        tip: 'Design models are "appearance-focused," while CAD models are "dimension and function-focused" - this is easy to remember.'
      },
      ai: {
        title: '3. AI Automatic Generation',
        intro: 'Recently, with the advancement of AI technology, services that can automatically generate 3D models from text or images have emerged.',
        current: 'Current Status and Possibilities',
        currentList: [
          'Generate 3D models from text',
          'Technology to convert images to 3D',
          'Still developing technology',
          'Practical for small items and simple shapes'
        ],
        cautions: 'Cautions',
        cautionsList: [
          'Not good at complex shapes',
          'Often requires corrections',
          'Cannot expect precise dimensions',
          'Convenient for prototype creation'
        ],
        tip: 'AI generation is a field where further evolution is expected in the future. Currently, auxiliary use is recommended.'
      }
    },
    tools: {
      title: 'Useful Tools for 3D Printing Work',
      precisionDriver: {
        title: 'Precision Screwdriver',
        use: 'Use: Fastening small screws, installing electronic components',
        sizes: 'Recommended Sizes: +00, +0, +1, -2.0, -3.0',
        tip: 'Magnetic tip is convenient, choose ESD-safe'
      },
      solderingIron: {
        title: 'Soldering Iron (for Correction)',
        use: 'Use: Insert nut insertion, surface correction',
        power: 'Recommended Output: 30-60W (with temperature control)',
        tip: 'Ventilation required, beware of burns'
      },
      nozzleTool: {
        title: 'Nozzle Replacement Tool',
        use: 'Use: Nozzle removal and installation, maintenance',
        types: 'Types: Socket wrench, dedicated tool',
        tip: 'Caution when working at high temperature, use appropriate tools'
      },
      deburringTool: {
        title: 'Deburring Tool',
        use: 'Use: Hole chamfering, deburring',
        types: 'Types: Rotary type, fixed type',
        tip: 'Pay attention to blade angle, use in safe direction'
      },
      clamps: {
        title: 'Clamps',
        use: 'Use: Fixing during processing, pressing during bonding',
        types: 'Types: C-clamp, bar clamp, hand vice',
        tip: 'Use appropriate clamping force, be careful not to damage material'
      },
      glueStick: {
        title: 'Glue Stick (Stationery)',
        use: 'Use: Regular "glue stick" that can be used when you want to slightly improve adhesion of printed objects.',
        desc: 'Regular ones sold in stationery sections work well enough. By applying thinly to glass beds or steel plates, model adhesion becomes slightly more stable.',
        tip: 'Apply thinly and evenly, wipe with water after printing'
      },
      magigoo: {
        title: 'Magigoo (3D Printer Adhesive)',
        use: 'Use: For stronger adhesion, dedicated 3D printer adhesives like "Magigoo" are also convenient.',
        desc: 'By applying to the bed, many types adhere firmly during printing and naturally peel off easily when cooled.',
        tip: 'High effectiveness as dedicated product, price is somewhat high'
      }
    },
    benchy: {
      title: '3DBenchy: Standard Test Model for 3D Printer Performance Comparison',
      intro: '3DBenchy is a standard model for 3D printer performance testing in the shape of a small boat. It is used worldwide.',
      testItems: 'Testable Items',
      testItemsList: [
        'Overhang performance',
        'Bridging capability',
        'Detail reproduction',
        'Surface finish',
        'Dimensional accuracy'
      ],
      usage: 'How to Use',
      usageList: [
        'Free download available',
        'Print time: about 1-2 hours',
        'Ideal for checking effects of setting changes',
        'Can be used for performance comparison between models'
      ]
    }
  } : {
    pageTitle: '役立ち関連知識',
    postProcessing: {
      title: '加工・後処理の基礎',
      support: {
        title: 'サポート材とは何か',
        intro: 'サポート材は、3Dプリント時に宙に浮いた部分や急な角度の部分を支えるための補助構造です。プリント後に取り除く必要があります。',
        whenNeeded: 'サポートが必要な場合',
        whenNeededList: [
          'オーバーハング角度が45度以上の部分',
          '宙に浮いた構造（橋状の部分など）',
          '細い突起物や複雑な形状'
        ],
        removalSteps: 'サポート除去の基本手順',
        removalStepsList: [
          'ニッパーやペンチで大まかに除去',
          'カッターで細かい部分を切り取る',
          'ヤスリで跡を滑らかに仕上げる'
        ]
      },
      sanding: {
        title: 'ヤスリがけの基礎',
        intro: 'ヤスリの粗さは番手（#）で表され、数字が大きいほど細かくなります。段階的に細かくしていくのが基本です。',
        coarse: '#120〜#240（粗目）',
        coarseDesc: 'サポート材跡の除去、大きな凹凸の修正に使用します。削る力が強いため、形を整える最初の段階で使います。',
        medium: '#400〜#600（中目）',
        mediumDesc: '表面の平滑化、積層痕の軽減に使用します。粗目で整えた後の中間仕上げに適しています。',
        fine: '#800〜#1200（細目）',
        fineDesc: '仕上げ研磨、塗装前の下地処理に使用します。表面を滑らかにして光沢を出すことができます。',
        tip: '水研ぎペーパーを使用すると粉塵を抑えられ、より滑らかな仕上がりになります。'
      },
      deburring: {
        title: 'カッターでのバリ取り',
        intro: 'バリとは、プリント時にできる糸状の突起や、サポート除去後に残る小さな突起のことです。カッターで丁寧に除去します。',
        basicUse: '基本的な使い方',
        basicUseList: [
          '刃を寝かせて、削ぎ取るように使う',
          '少しずつ削り、削りすぎに注意',
          '角を落とす面取りにも使える'
        ],
        safety: '安全な作業方法',
        safetyList: [
          '刃は必ず体から離す方向に動かす',
          '作業台を使い、手で持たない',
          '刃が鈍ったらすぐに交換する'
        ]
      },
      heatGun: {
        title: 'ヒートガンを使った簡易仕上げ',
        intro: 'ヒートガンで表面を軽く溶かすことで、積層痕を目立たなくし、滑らかな仕上がりにできます。',
        effects: '効果',
        effectsList: [
          '積層痕を目立たなくする',
          '表面に光沢を出す',
          'ヤスリがけの手間を減らせる'
        ],
        cautions: '注意点',
        cautionsList: [
          '一箇所に当て続けない（変形の原因）',
          '換気の良い場所で作業する',
          '材料によって適温が異なる'
        ]
      },
      rotaryTool: {
        title: 'ルーター（リューター）での微調整',
        intro: '電動の小型研削工具で、細かい部分の削り出しや穴あけ、研磨などに使用します。',
        uses: '主な用途',
        usesList: [
          '細かい部分の削り出し',
          '穴の拡張や形状修正',
          '研磨ビットでの仕上げ',
          '彫刻や装飾加工'
        ],
        bits: 'ビットの種類',
        bitsList: [
          '砥石ビット：削り出し用',
          'ダイヤモンドビット：硬い材料用',
          'フェルトビット：研磨仕上げ用',
          'カッティングビット：切断用'
        ]
      },
      painting: {
        title: '塗装の超基礎',
        intro: '塗装は3つの工程に分かれます。それぞれの工程を丁寧に行うことで、美しい仕上がりになります。',
        primer: '1. プライマー（下地）',
        primerDesc: '塗料の密着を良くし、発色を向上させる下地処理です。',
        primerList: [
          'ヤスリがけで表面を整える',
          '脱脂剤で油分を除去',
          'プライマーを薄く均一に塗る',
          '完全に乾燥させる'
        ],
        paint: '2. 塗装（本塗り）',
        paintDesc: '色を付ける工程です。薄く重ね塗りするのがコツです。',
        paintList: [
          '1回で厚塗りせず、薄く塗る',
          '乾燥時間を守る（15〜30分）',
          '2〜3回重ね塗りする',
          'ムラがないか確認する'
        ],
        clear: '3. クリア（仕上げ）',
        clearDesc: '保護と光沢を出すための透明な塗装です。',
        clearList: [
          '本塗りが完全に乾いてから',
          '光沢あり/なしを選べる',
          '薄く2回程度塗る',
          '24時間以上乾燥させる'
        ],
        tip: '必ず換気の良い場所で作業し、マスクを着用してください。スプレー塗装は屋外が理想的です。'
      }
    },
    screws: {
      title: 'ネジと締結の基礎',
      types: {
        title: 'ネジの種類',
        intro: '3Dプリント品の締結には、主に2種類のネジが使われます。用途に応じて使い分けましょう。',
        tapping: 'タッピンネジ',
        tappingDesc: '樹脂に直接ねじ込めるネジです。自分でネジ山を作りながら締まります。',
        tappingFeatures: '特徴：',
        tappingFeaturesList: [
          '下穴だけで使える（ネジ山不要）',
          '簡単に取り付けられる',
          '何度も脱着すると緩くなる'
        ],
        tappingSuited: '適した用途：',
        tappingSuitedList: [
          '一度組んだら外さない部分',
          '軽い負荷の締結',
          '仮組みやプロトタイプ'
        ],
        machine: '小ねじ（ボルト）',
        machineDesc: 'ナットや金属にネジ山が必要なネジです。インサートナットと組み合わせて使います。',
        machineFeatures: '特徴：',
        machineFeaturesList: [
          'ナットやインサートナットが必要',
          '何度でも脱着できる',
          '強固な締結が可能'
        ],
        machineSuited: '適した用途：',
        machineSuitedList: [
          'メンテナンスで外す部分',
          '強度が必要な締結',
          '製品レベルの組み立て'
        ]
      },
      insertNuts: {
        title: 'インサートナットとは何か',
        intro: 'インサートナット（熱圧入ナット）は、熱で樹脂に埋め込んで使える真鍮製のナットです。3Dプリント品に金属のネジ穴を作ることができ、非常に相性の良い締結方法です。',
        benefits: 'メリット',
        benefitsList: [
          '何度でも脱着できる',
          'ネジ山が傷まない',
          '強固な締結が可能',
          'プロ品質の仕上がり'
        ],
        tools: '必要な工具',
        toolsList: [
          'はんだごて（40W程度）',
          'インサートナット',
          '適切な下穴（設計時）'
        ],
        steps: '取り付け手順',
        stepsList: [
          'はんだごてで加熱',
          '垂直に押し込む',
          '表面と同じ高さまで',
          '冷却まで固定'
        ],
        tip: 'インサートナットは3Dプリント品を製品レベルに仕上げる重要なパーツです。メンテナンスが必要な部分には必ず使いましょう。'
      },
      sizes: {
        title: 'M3 / M2 などの意味',
        intro: 'ネジのサイズ表記で「M」はメートルねじの規格を示し、その後の数字は太さ（直径）を表します。',
        reading: 'サイズの読み方',
        m2: 'M2: 直径2mmのネジ',
        m3: 'M3: 直径3mmのネジ',
        m4: 'M4: 直径4mmのネジ',
        m5: 'M5: 直径5mmのネジ',
        common: '3Dプリントでよく使うサイズ',
        m2Use: 'M2: 小型電子機器、精密部品、ミニチュア模型など',
        m3Use: 'M3: 最も一般的。小〜中型の部品、電子工作、ホビー用途',
        m4Use: 'M4: 中型部品、やや強度が必要な箇所',
        m5Use: 'M5: 大型部品、構造材、高負荷がかかる部分'
      }
    },
    dataAcquisition: {
      title: '3Dデータの入手方法',
      intro: '3Dプリントに必要な3Dデータは、主に3つの方法で入手できます。目的に応じて使い分けましょう。',
      download: {
        title: '1. ネットでダウンロード',
        intro: '3Dプリント向けのモデル掲載サイトが多数あり、無料・有料問わず膨大な数のモデルが公開されています。',
        thingiverse: 'Thingiverse: 最大級の無料3Dモデル共有サイト。ホビーから実用品まで幅広い。',
        thingiverseFeature: '特徴：完全無料、コミュニティ活発',
        printables: 'Printables: Prusa Research運営の無料サイト。高品質なモデルが多い。',
        printablesFeature: '特徴：品質重視、コンテスト開催',
        cults: 'Cults3D: 無料・有料混在。デザイン性の高いモデルが豊富。',
        cultsFeature: '特徴：デザイナー作品、有料モデルあり',
        nextprint: 'Nextprint: ELEGOO が運営する 3D モデル共有プラットフォーム。世界中のユーザーが STL などの3Dデータをアップロード・ダウンロードできる。',
        nextprintFeature: '特徴：ELEGOO 製プリンター向けのプロファイルやワンクリック印刷に対応しつつ、他社プリンターでも使いやすい汎用的な3Dモデルが公開されている。',
        tip: 'ダウンロードしたモデルは、ライセンスを確認してから使用しましょう。商用利用が禁止されている場合もあります。'
      },
      design: {
        title: '2. 自分でデザイン',
        intro: '自分でモデルを作成すれば、完全にオリジナルの形状を実現できます。用途に応じて2種類のソフトを使い分けます。',
        blender: 'デザインモデル：Blender',
        blenderFeature: '特徴：自由な形状作成が得意。フィギュア、キャラクター、有機的な形状に向いています。',
        blenderSuited: '向いている用途：',
        blenderSuitedList: [
          'フィギュア・キャラクター',
          '装飾品・アート作品',
          '彫刻的な形状',
          'アニメーション制作'
        ],
        blenderConcept: '形状作成の考え方：粘土をこねるように、頂点・辺・面を操作して形を作る',
        fusion: 'CADモデル：Autodesk Fusion（旧Fusion360）',
        fusionFeature: '特徴：正確な寸法で設計できる。機械部品、実用品、組み立て式の製品に向いています。',
        fusionSuited: '向いている用途：',
        fusionSuitedList: [
          '機械部品・工業製品',
          'ネジ穴などの精密加工',
          '組み立て式の製品',
          '実用的な道具・治具'
        ],
        fusionConcept: '形状作成の考え方：スケッチを描いて押し出す、回転させるなど、工学的な手法で形を作る',
        tip: 'デザインモデルは「見た目重視」、CADモデルは「寸法・機能重視」と覚えておくと分かりやすいです。'
      },
      ai: {
        title: '3. AIによる自動生成',
        intro: '最近はAI技術の進化により、テキストや画像から3Dモデルを自動生成できるサービスが登場しています。',
        current: '現状と可能性',
        currentList: [
          'テキストから3Dモデルを生成',
          '画像から3D化する技術',
          'まだ発展途上の技術',
          '小物や簡単な形状なら実用的'
        ],
        cautions: '注意点',
        cautionsList: [
          '複雑な形状は苦手',
          '修正が必要な場合が多い',
          '精密な寸法は期待できない',
          'プロトタイプ作成には便利'
        ],
        tip: 'AI生成は今後さらに進化が期待される分野です。現時点では補助的な使い方がおすすめです。'
      }
    },
    tools: {
      title: '3Dプリント作業に役立つ工具紹介',
      precisionDriver: {
        title: '精密ドライバー',
        use: '用途：細かいネジの締結、電子部品の取り付け',
        sizes: '推奨サイズ：+00, +0, +1, -2.0, -3.0',
        tip: '磁石付きが便利、静電気対策済みを選ぶ'
      },
      solderingIron: {
        title: 'はんだごて（修正用）',
        use: '用途：インサートナット挿入、表面修正',
        power: '推奨出力：30〜60W（温度調整機能付き）',
        tip: '換気必須、やけど注意'
      },
      nozzleTool: {
        title: 'ノズル交換工具',
        use: '用途：ノズルの着脱、メンテナンス',
        types: '種類：ソケットレンチ、専用工具',
        tip: '高温時の作業注意、適切な工具を使用'
      },
      deburringTool: {
        title: 'デバリングツール',
        use: '用途：穴の面取り、バリ取り',
        types: '種類：回転式、固定式',
        tip: '刃の角度に注意、安全な方向に使用'
      },
      clamps: {
        title: 'クランプ類',
        use: '用途：加工時の固定、接着時の圧着',
        types: '種類：Cクランプ、バークランプ、ハンドバイス',
        tip: '適切な締め付け力で、材料を傷つけないよう注意'
      },
      glueStick: {
        title: 'スティックのり（文房具用）',
        use: '用途：造形物の定着を少しだけ良くしたいときに使える、いわゆる普通の「スティックのり」です。',
        desc: '文房具コーナーに売っている一般的なものでも十分使えます。ガラスベッドやスチールプレートに薄く塗ることで、モデルの食いつきが少し安定しやすくなります。',
        tip: '薄く均一に塗る、造形後は水で清拭'
      },
      magigoo: {
        title: 'Magigoo（3Dプリンター用接着剤）',
        use: '用途：もっとしっかり定着させたい場合は、3Dプリンター用の専用接着剤「Magigoo」のような製品も便利です。',
        desc: 'ベッドに塗っておくことで、造形中はしっかり密着し、冷えると自然に剥がれやすくなるタイプのものが多いです。',
        tip: '専用品なので効果は高い、価格はやや高め'
      }
    },
    benchy: {
      title: '3DBenchy：3Dプリンター性能比較の標準テストモデル',
      intro: '3DBenchyは、小さな船の形をした3Dプリンター性能テストの標準モデルです。世界中で使われています。',
      testItems: 'テストできる項目',
      testItemsList: [
        'オーバーハング性能',
        'ブリッジング能力',
        '細部の再現性',
        '表面の仕上がり',
        '寸法精度'
      ],
      usage: '使い方',
      usageList: [
        '無料でダウンロード可能',
        'プリント時間：約1〜2時間',
        '設定変更の効果確認に最適',
        '機種間の性能比較に使える'
      ]
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* ページタイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.pageTitle}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 加工・後処理の基礎 */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-scissors-2-line text-accent mr-3"></i>
                {TEXT.postProcessing.title}
              </h2>
              <div className="space-y-6 text-metal-light">
                {/* サポート材とは */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-building-line text-accent mr-2"></i>
                    {TEXT.postProcessing.support.title}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <p>{TEXT.postProcessing.support.intro}</p>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.support.whenNeeded}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.support.whenNeededList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.support.removalSteps}</h4>
                        <ol className="space-y-1 text-xs list-decimal list-inside">
                          {TEXT.postProcessing.support.removalStepsList.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ヤスリがけの基礎 */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-file-line text-accent mr-2"></i>
                    {TEXT.postProcessing.sanding.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.postProcessing.sanding.intro}</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.sanding.coarse}</h4>
                        <p className="text-xs">{TEXT.postProcessing.sanding.coarseDesc}</p>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.sanding.medium}</h4>
                        <p className="text-xs">{TEXT.postProcessing.sanding.mediumDesc}</p>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.sanding.fine}</h4>
                        <p className="text-xs">{TEXT.postProcessing.sanding.fineDesc}</p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">
                        <i className="ri-information-line mr-1"></i>
                        {TEXT.postProcessing.sanding.tip}
                      </p>
                    </div>
                  </div>
                </div>

                {/* カッターでのバリ取り */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-scissors-cut-line text-accent mr-2"></i>
                    {TEXT.postProcessing.deburring.title}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <p>{TEXT.postProcessing.deburring.intro}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.deburring.basicUse}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.deburring.basicUseList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.deburring.safety}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.deburring.safetyList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ヒートガンを使った簡易仕上げ */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-fire-line text-accent mr-2"></i>
                    {TEXT.postProcessing.heatGun.title}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <p>{TEXT.postProcessing.heatGun.intro}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.heatGun.effects}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.heatGun.effectsList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.heatGun.cautions}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.heatGun.cautionsList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ルーター（リューター）での微調整 */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-tools-line text-accent mr-2"></i>
                    {TEXT.postProcessing.rotaryTool.title}
                  </h3>
                  <div className="space-y-3 text-sm">
                    <p>{TEXT.postProcessing.rotaryTool.intro}</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.rotaryTool.uses}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.rotaryTool.usesList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.postProcessing.rotaryTool.bits}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.rotaryTool.bitsList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 塗装の超基礎 */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-brush-line text-accent mr-2"></i>
                    {TEXT.postProcessing.painting.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.postProcessing.painting.intro}</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.postProcessing.painting.primer}</h4>
                        <p className="text-xs mb-2">{TEXT.postProcessing.painting.primerDesc}</p>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.painting.primerList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.postProcessing.painting.paint}</h4>
                        <p className="text-xs mb-2">{TEXT.postProcessing.painting.paintDesc}</p>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.painting.paintList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.postProcessing.painting.clear}</h4>
                        <p className="text-xs mb-2">{TEXT.postProcessing.painting.clearDesc}</p>
                        <ul className="space-y-1 text-xs">
                          {TEXT.postProcessing.painting.clearList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">
                        <i className="ri-information-line mr-1"></i>
                        {TEXT.postProcessing.painting.tip}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ネジと締結の基礎 */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-hammer-line text-accent mr-3"></i>
                {TEXT.screws.title}
              </h2>
              <div className="space-y-6 text-metal-light">
                {/* ネジの種類 */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-settings-3-line text-accent mr-2"></i>
                    {TEXT.screws.types.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.screws.types.intro}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3 text-base">{TEXT.screws.types.tapping}</h4>
                        <p className="text-xs mb-3">{TEXT.screws.types.tappingDesc}</p>
                        <div className="space-y-2">
                          <p className="text-xs"><strong className="text-white">{TEXT.screws.types.tappingFeatures}</strong></p>
                          <ul className="space-y-1 text-xs ml-4">
                            {TEXT.screws.types.tappingFeaturesList.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                          <p className="text-xs mt-3"><strong className="text-white">{TEXT.screws.types.tappingSuited}</strong></p>
                          <ul className="space-y-1 text-xs ml-4">
                            {TEXT.screws.types.tappingSuitedList.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3 text-base">{TEXT.screws.types.machine}</h4>
                        <p className="text-xs mb-3">{TEXT.screws.types.machineDesc}</p>
                        <div className="space-y-2">
                          <p className="text-xs"><strong className="text-white">{TEXT.screws.types.machineFeatures}</strong></p>
                          <ul className="space-y-1 text-xs ml-4">
                            {TEXT.screws.types.machineFeaturesList.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                          <p className="text-xs mt-3"><strong className="text-white">{TEXT.screws.types.machineSuited}</strong></p>
                          <ul className="space-y-1 text-xs ml-4">
                            {TEXT.screws.types.machineSuitedList.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* インサートナットとは何か */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-checkbox-blank-circle-line text-accent mr-2"></i>
                    {TEXT.screws.insertNuts.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.screws.insertNuts.intro}</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.screws.insertNuts.benefits}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.screws.insertNuts.benefitsList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.screws.insertNuts.tools}</h4>
                        <ul className="space-y-1 text-xs">
                          {TEXT.screws.insertNuts.toolsList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">{TEXT.screws.insertNuts.steps}</h4>
                        <ol className="space-y-1 text-xs list-decimal list-inside">
                          {TEXT.screws.insertNuts.stepsList.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ol>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">
                        <i className="ri-information-line mr-1"></i>
                        {TEXT.screws.insertNuts.tip}
                      </p>
                    </div>
                  </div>
                </div>

                {/* M3 / M2 などの意味 */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-ruler-line text-accent mr-2"></i>
                    {TEXT.screws.sizes.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.screws.sizes.intro}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.screws.sizes.reading}</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="bg-accent/20 px-3 py-2 font-mono text-white">M2</div>
                            <div className="text-xs">{TEXT.screws.sizes.m2}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="bg-accent/20 px-3 py-2 font-mono text-white">M3</div>
                            <div className="text-xs">{TEXT.screws.sizes.m3}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="bg-accent/20 px-3 py-2 font-mono text-white">M4</div>
                            <div className="text-xs">{TEXT.screws.sizes.m4}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="bg-accent/20 px-3 py-2 font-mono text-white">M5</div>
                            <div className="text-xs">{TEXT.screws.sizes.m5}</div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.screws.sizes.common}</h4>
                        <div className="space-y-3 text-xs">
                          <div>
                            <p className="text-white font-semibold mb-1">M2</p>
                            <p>{TEXT.screws.sizes.m2Use}</p>
                          </div>
                          <div>
                            <p className="text-white font-semibold mb-1">M3</p>
                            <p>{TEXT.screws.sizes.m3Use}</p>
                          </div>
                          <div>
                            <p className="text-white font-semibold mb-1">M4</p>
                            <p>{TEXT.screws.sizes.m4Use}</p>
                          </div>
                          <div>
                            <p className="text-white font-semibold mb-1">M5</p>
                            <p>{TEXT.screws.sizes.m5Use}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 3Dデータの入手方法 */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-download-cloud-line text-accent mr-3"></i>
                {TEXT.dataAcquisition.title}
              </h2>
              <div className="space-y-6 text-metal-light">
                <p className="text-sm">{TEXT.dataAcquisition.intro}</p>
                
                {/* ネットでダウンロード */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-global-line text-accent mr-2"></i>
                    {TEXT.dataAcquisition.download.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.dataAcquisition.download.intro}</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">Thingiverse</h4>
                        <p className="text-xs mb-2">{TEXT.dataAcquisition.download.thingiverse}</p>
                        <p className="text-xs text-white">{TEXT.dataAcquisition.download.thingiverseFeature}</p>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">Printables</h4>
                        <p className="text-xs mb-2">{TEXT.dataAcquisition.download.printables}</p>
                        <p className="text-xs text-white">{TEXT.dataAcquisition.download.printablesFeature}</p>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">Cults3D</h4>
                        <p className="text-xs mb-2">{TEXT.dataAcquisition.download.cults}</p>
                        <p className="text-xs text-white">{TEXT.dataAcquisition.download.cultsFeature}</p>
                      </div>
                      <div className="bg-metal/30 p-4 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-2">Nextprint</h4>
                        <p className="text-xs mb-2">{TEXT.dataAcquisition.download.nextprint}</p>
                        <p className="text-xs text-white">{TEXT.dataAcquisition.download.nextprintFeature}</p>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">
                        <i className="ri-information-line mr-1"></i>
                        {TEXT.dataAcquisition.download.tip}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 自分でデザイン */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-pencil-ruler-2-line text-accent mr-2"></i>
                    {TEXT.dataAcquisition.design.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.dataAcquisition.design.intro}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3 text-base flex items-center">
                          <i className="ri-palette-line mr-2"></i>
                          {TEXT.dataAcquisition.design.blender}
                        </h4>
                        <div className="space-y-3 text-xs">
                          <p><strong className="text-white">{isEnglish ? 'Features:' : '特徴：'}</strong>{TEXT.dataAcquisition.design.blenderFeature.replace(/^特徴：|^Features:/, '')}</p>
                          <p><strong className="text-white">{TEXT.dataAcquisition.design.blenderSuited}</strong></p>
                          <ul className="space-y-1 ml-4">
                            {TEXT.dataAcquisition.design.blenderSuitedList.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                          <p className="text-white mt-2"><strong>{isEnglish ? 'Shape Creation Concept:' : '形状作成の考え方：'}</strong>{TEXT.dataAcquisition.design.blenderConcept.replace(/^形状作成の考え方：|^Shape Creation Concept:/, '')}</p>
                        </div>
                      </div>
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3 text-base flex items-center">
                          <i className="ri-compasses-2-line mr-2"></i>
                          {TEXT.dataAcquisition.design.fusion}
                        </h4>
                        <div className="space-y-3 text-xs">
                          <p><strong className="text-white">{isEnglish ? 'Features:' : '特徴：'}</strong>{TEXT.dataAcquisition.design.fusionFeature.replace(/^特徴：|^Features:/, '')}</p>
                          <p><strong className="text-white">{TEXT.dataAcquisition.design.fusionSuited}</strong></p>
                          <ul className="space-y-1 ml-4">
                            {TEXT.dataAcquisition.design.fusionSuitedList.map((item, i) => (
                              <li key={i}>• {item}</li>
                            ))}
                          </ul>
                          <p className="text-white mt-2"><strong>{isEnglish ? 'Shape Creation Concept:' : '形状作成の考え方：'}</strong>{TEXT.dataAcquisition.design.fusionConcept.replace(/^形状作成の考え方：|^Shape Creation Concept:/, '')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">
                        <i className="ri-information-line mr-1"></i>
                        {TEXT.dataAcquisition.design.tip}
                      </p>
                    </div>
                  </div>
                </div>

                {/* AIによる自動生成 */}
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-robot-line text-accent mr-2"></i>
                    {TEXT.dataAcquisition.ai.title}
                  </h3>
                  <div className="space-y-4 text-sm">
                    <p>{TEXT.dataAcquisition.ai.intro}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.dataAcquisition.ai.current}</h4>
                        <ul className="space-y-2 text-xs">
                          {TEXT.dataAcquisition.ai.currentList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-metal/30 p-5 border border-accent/30">
                        <h4 className="text-accent font-semibold mb-3">{TEXT.dataAcquisition.ai.cautions}</h4>
                        <ul className="space-y-2 text-xs">
                          {TEXT.dataAcquisition.ai.cautionsList.map((item, i) => (
                            <li key={i}>• {item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">
                        <i className="ri-information-line mr-1"></i>
                        {TEXT.dataAcquisition.ai.tip}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 3Dプリント作業に役立つ工具紹介 */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-tools-line text-accent mr-3"></i>
                {TEXT.tools.title}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-tools-fill text-accent mr-2"></i>
                    {TEXT.tools.precisionDriver.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.precisionDriver.use.replace(/^用途：|^Use:/, '')}</p>
                    <p><strong className="text-white">{isEnglish ? 'Recommended Sizes:' : '推奨サイズ：'}</strong>{TEXT.tools.precisionDriver.sizes.replace(/^推奨サイズ：|^Recommended Sizes:/, '')}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.precisionDriver.tip}</p>
                    </div>
                  </div>
                </div>
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-flashlight-line text-accent mr-2"></i>
                    {TEXT.tools.solderingIron.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.solderingIron.use.replace(/^用途：|^Use:/, '')}</p>
                    <p><strong className="text-white">{isEnglish ? 'Recommended Output:' : '推奨出力：'}</strong>{TEXT.tools.solderingIron.power.replace(/^推奨出力：|^Recommended Output:/, '')}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.solderingIron.tip}</p>
                    </div>
                  </div>
                </div>
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-settings-2-line text-accent mr-2"></i>
                    {TEXT.tools.nozzleTool.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.nozzleTool.use.replace(/^用途：|^Use:/, '')}</p>
                    <p><strong className="text-white">{isEnglish ? 'Types:' : '種類：'}</strong>{TEXT.tools.nozzleTool.types.replace(/^種類：|^Types:/, '')}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.nozzleTool.tip}</p>
                    </div>
                  </div>
                </div>
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-knife-line text-accent mr-2"></i>
                    {TEXT.tools.deburringTool.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.deburringTool.use.replace(/^用途：|^Use:/, '')}</p>
                    <p><strong className="text-white">{isEnglish ? 'Types:' : '種類：'}</strong>{TEXT.tools.deburringTool.types.replace(/^種類：|^Types:/, '')}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.deburringTool.tip}</p>
                    </div>
                  </div>
                </div>
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-compasses-2-line text-accent mr-2"></i>
                    {TEXT.tools.clamps.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.clamps.use.replace(/^用途：|^Use:/, '')}</p>
                    <p><strong className="text-white">{isEnglish ? 'Types:' : '種類：'}</strong>{TEXT.tools.clamps.types.replace(/^種類：|^Types:/, '')}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.clamps.tip}</p>
                    </div>
                  </div>
                </div>
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-sticky-note-line text-accent mr-2"></i>
                    {TEXT.tools.glueStick.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.glueStick.use.replace(/^用途：|^Use:/, '')}</p>
                    <p>{TEXT.tools.glueStick.desc}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.glueStick.tip}</p>
                    </div>
                  </div>
                </div>
                <div className="static-card p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <i className="ri-drop-line text-accent mr-2"></i>
                    {TEXT.tools.magigoo.title}
                  </h3>
                  <div className="space-y-3 text-sm text-metal-light">
                    <p><strong className="text-white">{isEnglish ? 'Use:' : '用途：'}</strong>{TEXT.tools.magigoo.use.replace(/^用途：|^Use:/, '')}</p>
                    <p>{TEXT.tools.magigoo.desc}</p>
                    <div className="mt-3 p-2 bg-accent/10 border border-accent/30">
                      <p className="text-xs text-accent">{TEXT.tools.magigoo.tip}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 3DBenchy */}
            <section className="tech-card p-8">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-6 flex items-center">
                <i className="ri-ship-line text-accent mr-3"></i>
                {TEXT.benchy.title}
              </h2>
              <div className="space-y-3 text-sm text-metal-light">
                <p>{TEXT.benchy.intro}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-metal/30 p-4 border border-accent/30">
                    <h4 className="text-accent font-semibold mb-2">{TEXT.benchy.testItems}</h4>
                    <ul className="space-y-1 text-xs">
                      {TEXT.benchy.testItemsList.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-metal/30 p-4 border border-accent/30">
                    <h4 className="text-accent font-semibold mb-2">{TEXT.benchy.usage}</h4>
                    <ul className="space-y-1 text-xs">
                      {TEXT.benchy.usageList.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default KnowledgePage;